import React, { memo } from 'react';
import '../componentStyle/WaitingRoomScreen.css';

// Memoized to prevent unnecessary re-renders when parent updates
const WaitingRoomScreen = memo(function WaitingRoomScreen({ onLeave, participantName }) {
    return (
        <div className="waiting-room-container">
            <div className="waiting-room-content">
                {/* Animated Illustration */}
                <div className="waiting-illustration">
                    <svg
                        width="400"
                        height="400"
                        viewBox="0 0 400 400"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        {/* Clock on wall */}
                        <g className="clock-group">
                            <circle cx="320" cy="80" r="25" fill="#4a5d8f" opacity="0.8" />
                            <circle cx="320" cy="80" r="22" fill="#5b7fd6" className="clock-pulse" />
                            {/* Clock hands */}
                            <line x1="320" y1="80" x2="320" y2="65" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            <line
                                x1="320"
                                y1="80"
                                x2="330"
                                y2="75"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                className="clock-hand"
                            />
                        </g>

                        {/* Background furniture - Cabinet */}
                        <g className="cabinet">
                            <rect x="120" y="130" width="100" height="180" fill="#2a2a2a" rx="4" />
                            <rect x="130" y="140" width="35" height="80" fill="#1f1f1f" rx="2" />
                            <rect x="175" y="140" width="35" height="80" fill="#1f1f1f" rx="2" />
                            <rect x="130" y="235" width="80" height="60" fill="#1f1f1f" rx="2" />
                            <line x1="147.5" y1="170" x2="147.5" y2="180" stroke="#888" strokeWidth="1.5" />
                            <line x1="192.5" y1="170" x2="192.5" y2="180" stroke="#888" strokeWidth="1.5" />
                            <circle cx="170" cy="265" r="3" fill="#888" />
                        </g>

                        {/* Chair */}
                        <g className="chair">
                            <rect x="140" y="320" width="50" height="8" fill="#2a2a2a" rx="2" />
                            <rect x="148" y="285" width="34" height="35" fill="#2a2a2a" rx="2" />
                            <rect x="148" y="260" width="34" height="30" fill="#2a2a2a" rx="2" />
                            <rect x="155" y="328" width="4" height="30" fill="#2a2a2a" />
                            <rect x="171" y="328" width="4" height="30" fill="#2a2a2a" />
                        </g>

                        {/* Plant pot */}
                        <g className="plant-group">
                            <path d="M 110 360 L 120 340 L 140 340 L 150 360 Z" fill="#5b7fd6" />
                            <rect x="115" y="360" width="30" height="5" fill="#4a5d8f" />
                            {/* Plant leaves with animation */}
                            <ellipse cx="120" cy="330" rx="8" ry="15" fill="#7a8a9a" className="leaf leaf-1" />
                            <ellipse cx="130" cy="325" rx="10" ry="18" fill="#8a9aaa" className="leaf leaf-2" />
                            <ellipse cx="140" cy="330" rx="8" ry="16" fill="#7a8a9a" className="leaf leaf-3" />
                            <ellipse cx="125" cy="315" rx="9" ry="17" fill="#9aaaba" className="leaf leaf-4" />
                            <ellipse cx="135" cy="318" rx="7" ry="14" fill="#8a9aaa" className="leaf leaf-5" />
                            <ellipse cx="130" cy="305" rx="8" ry="15" fill="#9aaaba" className="leaf leaf-6" />
                        </g>

                        {/* Person */}
                        <g className="person-group">
                            {/* Legs */}
                            <rect x="230" y="290" width="22" height="80" fill="#b8b8b8" rx="2" className="person-body" />
                            <rect x="258" y="290" width="22" height="80" fill="#a8a8a8" rx="2" className="person-body" />
                            {/* Shoes */}
                            <ellipse cx="241" cy="370" rx="12" ry="5" fill="#5a5a5a" />
                            <ellipse cx="269" cy="370" rx="12" ry="5" fill="#5a5a5a" />

                            {/* Body */}
                            <path
                                d="M 225 230 Q 225 220 230 215 L 230 260 Q 230 270 235 275 L 235 290 L 275 290 L 275 275 Q 280 270 280 260 L 280 215 Q 285 220 285 230 L 285 285 L 225 285 Z"
                                fill="#5b7fd6"
                                className="person-body"
                            />

                            {/* Belt */}
                            <rect x="225" y="285" width="60" height="8" fill="#4a4a4a" />
                            <rect x="252" y="285" width="6" height="8" fill="#888" />

                            {/* Right arm (pointing at door) */}
                            <g className="person-arm-right">
                                <ellipse cx="225" cy="245" rx="10" ry="28" fill="#4a6db8" transform="rotate(-25 225 245)" />
                                <ellipse cx="210" cy="235" rx="8" ry="18" fill="#c8b8a8" transform="rotate(-35 210 235)" />
                            </g>

                            {/* Left arm */}
                            <ellipse cx="285" cy="250" rx="10" ry="30" fill="#5b7fd6" transform="rotate(15 285 250)" />
                            <ellipse cx="295" cy="275" rx="8" ry="16" fill="#c8b8a8" transform="rotate(20 295 275)" />

                            {/* Neck */}
                            <rect x="245" y="205" width="20" height="15" fill="#d8c8b8" rx="2" />

                            {/* Head */}
                            <ellipse cx="255" cy="195" rx="22" ry="25" fill="#d8c8b8" className="person-head" />

                            {/* Hair */}
                            <path
                                d="M 235 185 Q 233 175 238 170 Q 245 165 255 165 Q 265 165 272 170 Q 277 175 275 185 Q 273 195 270 200 L 240 200 Q 237 195 235 185 Z"
                                fill="#c8c8c8"
                            />

                            {/* Ear */}
                            <ellipse cx="232" cy="195" rx="4" ry="6" fill="#c8b8a8" />

                            {/* Face details */}
                            <circle cx="248" cy="192" r="2" fill="#3a3a3a" className="person-eye" />
                            <circle cx="262" cy="192" r="2" fill="#3a3a3a" className="person-eye" />
                            <path d="M 250 205 Q 255 207 260 205" stroke="#b8a898" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        </g>

                        {/* Door */}
                        <g className="door-group">
                            <rect x="310" y="140" width="100" height="230" fill="#5b7fd6" rx="8" className="door-glow" />
                            <rect x="315" y="145" width="90" height="220" fill="#4a6db8" rx="6" opacity="0.3" />

                            {/* Door window */}
                            <rect x="335" y="165" width="50" height="70" fill="#2a3a5a" rx="4" />
                            <line x1="360" y1="165" x2="360" y2="235" stroke="#3a4a6a" strokeWidth="2" />
                            <line x1="335" y1="200" x2="385" y2="200" stroke="#3a4a6a" strokeWidth="2" />

                            {/* Door handle */}
                            <circle cx="330" cy="270" r="4" fill="#2a2a2a" />
                            <circle cx="330" cy="270" r="3" fill="#888" />
                        </g>
                    </svg>
                </div>

                {/* Text Content */}
                <div className="waiting-text">
                    {participantName && (
                        <p className="waiting-participant">Welcome, {participantName}!</p>
                    )}
                    <h1 className="waiting-title">
                        Waiting for Host Approval<span className="waiting-dots"></span>
                    </h1>
                    <p className="waiting-subtitle">
                        Hang tight! We're waiting for the host to open the door.
                    </p>
                </div>

                {/* Leave Button */}
                <button className="leave-meeting-btn" onClick={onLeave}>
                    Leave Meeting
                </button>
            </div>
        </div>
    );
});

export default WaitingRoomScreen;

